import { useMemo, useState } from 'react'
import {
  CATEGORIES_DEPENSE,
  codeCompteValide,
  COMPTE_DEPENSE,
  controlerEcritureManuelle,
  LIBELLE_JOURNAL,
  libelleCompte,
  planComplet,
} from '../../api/compta'
import { estVerrouille } from '../../api/cloture'
import {
  ajouterEcritureManuelle,
  enregistrerCompte,
  enregistrerDepense,
  modifierOrganisation,
  supprimerDepense,
  supprimerEcritureManuelle,
  type ReglagesOrganisation,
} from '../../api/operations'
import type { Journal } from '../../api/types'
import { useDroits } from '../Contexte'
import { dateCourte, fcfa } from '../Format'
import { Icone } from '../Icone'
import { ListeDeroulante } from '../ListeDeroulante'
import { Modale } from '../Modale'
import { Carte, CodeCompte, Table, type PropsCompta } from './briques'

const aujourdhui = () => new Date().toISOString().slice(0, 10)

/**
 * Paramètres — transposition de l'onglet `params` : régime & TVA, identité
 * fiscale, dépenses, écritures manuelles, plan de comptes.
 *
 * ⚠️ Dans l'original, TOUT ici était en mémoire : la TVA, le préfixe, les
 * libellés de comptes, les dépenses ajoutées et les écritures manuelles
 * disparaissaient au rechargement (et la TVA modifiée ne touchait jamais la
 * base). Ici chaque bloc écrit à un endroit réel :
 *   • identité + TVA → `organizations` (propriétaire seulement, 0016) ;
 *   • dépenses → edge function `record-expense` / `expenses` ;
 *   • écritures manuelles → `org_documents/ecritures_manuelles` ;
 *   • plan de comptes → `org_documents/plan_comptable`.
 */
export function Parametres(p: PropsCompta) {
  const { donnees } = p
  return (
    <>
      {!donnees.synchro.documents && (
        <div className="bandeau erreur" style={{ marginBottom: 14 }}>
          Les registres comptables (écritures manuelles, plan de comptes) n’ont pas pu être lus. Rien n’est affiché d’inventé,
          mais rechargez la page avant toute saisie : enregistrer maintenant pourrait écraser des écritures non chargées.
        </div>
      )}
      <Identite {...p} />
      <div className="grid-2">
        <Depenses {...p} />
        <EcrituresManuelles {...p} />
      </div>
      <PlanDeComptes {...p} />
    </>
  )
}

/* ------------------------------------------------------ identité & TVA */

function Identite({ donnees, orgId, surNotifier, surRafraichir }: PropsCompta) {
  const droits = useDroits()
  const modifiable = droits.peut('parametres_org')
  const o = donnees.org
  const [f, setF] = useState({
    name: o.name ?? '',
    legal_form: o.legal_form ?? '',
    nif: o.nif ?? '',
    rccm: o.rccm ?? '',
    address: o.address ?? '',
    phone: o.phone ?? '',
    email: o.email ?? '',
    invoice_prefix: o.invoice_prefix ?? 'FAC',
    vat_registered: o.vat_registered,
    vat_rate: o.vat_rate,
  })
  const [enCours, setEnCours] = useState(false)
  const changeTVA = f.vat_registered !== o.vat_registered || Number(f.vat_rate) !== Number(o.vat_rate)

  async function enregistrer() {
    if (!f.name.trim()) return surNotifier('La raison sociale est obligatoire')
    if (!/^[A-Z0-9-]{1,12}$/i.test(f.invoice_prefix.trim())) return surNotifier('Préfixe de facture : lettres, chiffres ou tiret, 12 caractères au plus')
    const taux = Number(f.vat_rate)
    if (!Number.isFinite(taux) || taux < 0 || taux > 30) return surNotifier('Taux de TVA entre 0 et 30 %')
    if (
      changeTVA &&
      !window.confirm(
        "Changer le régime ou le taux de TVA recalcule TOUS les états : le chiffre d'affaires des courses particuliers passe hors taxes (ou TTC) et la dette de TVA (443) apparaît ou disparaît, y compris sur le passé. Les factures déjà émises gardent leur TVA. Continuer ?",
      )
    )
      return
    const patch: ReglagesOrganisation = {
      name: f.name.trim(),
      legal_form: f.legal_form.trim() || null,
      nif: f.nif.trim() || null,
      rccm: f.rccm.trim() || null,
      address: f.address.trim() || null,
      phone: f.phone.trim() || null,
      email: f.email.trim() || null,
      invoice_prefix: f.invoice_prefix.trim().toUpperCase(),
      vat_registered: f.vat_registered,
      vat_rate: taux,
    }
    setEnCours(true)
    try {
      await modifierOrganisation(orgId, patch)
      surNotifier('Réglages enregistrés')
      await surRafraichir()
    } catch (e) {
      surNotifier(`Erreur : ${e instanceof Error ? e.message : String(e)}`)
    } finally {
      setEnCours(false)
    }
  }

  const champ = (cle: 'name' | 'legal_form' | 'nif' | 'rccm' | 'address' | 'phone' | 'email' | 'invoice_prefix', libelle: string, aide?: string) => (
    <div className="field" key={cle}>
      <label htmlFor={`org-${cle}`}>{libelle}</label>
      <input id={`org-${cle}`} value={f[cle]} disabled={!modifiable} onChange={(e) => setF({ ...f, [cle]: e.target.value })} placeholder={aide} />
    </div>
  )

  return (
    <Carte
      titre="Identité fiscale et TVA"
      icone="gear"
      sous="Mentions légales des factures (SYSCOHADA, DGI Mali) et régime de TVA appliqué aux écritures"
      actions={
        modifiable ? (
          <button className="btn btn-gold btn-sm" onClick={() => void enregistrer()} disabled={enCours}>
            {enCours ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        ) : (
          <span className="t-muted">{droits.refus} Lecture seule.</span>
        )
      }
      flush={false}
    >
      <div className="form form-grid">
        {champ('name', 'Raison sociale *')}
        {champ('legal_form', 'Forme juridique', 'SARL, SA, EI…')}
        {champ('nif', 'NIF (Numéro d’identification fiscale)')}
        {champ('rccm', 'RCCM')}
        {champ('address', 'Adresse')}
        {champ('phone', 'Téléphone')}
        {champ('email', 'E-mail')}
        {champ('invoice_prefix', 'Préfixe de facture', 'FAC')}
        <div className="field">
          <label>Régime de TVA</label>
          <button
            type="button"
            className={`interrupteur${f.vat_registered ? ' on' : ''}`}
            role="switch"
            aria-checked={f.vat_registered}
            disabled={!modifiable}
            onClick={() => setF({ ...f, vat_registered: !f.vat_registered })}
          >
            <span className={`bascule${f.vat_registered ? ' on' : ''}`} aria-hidden="true" />
            <span>{f.vat_registered ? 'Assujetti à la TVA' : 'Non assujetti'}</span>
          </button>
        </div>
        <div className="field">
          <label htmlFor="org-tva">Taux de TVA (%)</label>
          <input
            id="org-tva"
            type="number"
            min={0}
            max={30}
            step={0.5}
            value={f.vat_rate}
            disabled={!modifiable || !f.vat_registered}
            onChange={(e) => setF({ ...f, vat_rate: Number(e.target.value) })}
          />
        </div>
      </div>
      <p className="t-muted" style={{ marginTop: 10, fontSize: 12 }}>
        La TVA est toujours <b>extraite</b> des montants annoncés au client, jamais ajoutée. Le préfixe ne s’applique qu’aux
        prochaines factures (numérotation serveur, par année).
      </p>
    </Carte>
  )
}

/* ------------------------------------------------------------ dépenses */

function Depenses({ donnees, orgId, verrou, surNotifier, surRafraichir }: PropsCompta) {
  const droits = useDroits()
  const peut = droits.peut('depenses')
  const [ajout, setAjout] = useState(false)

  async function supprimer(id: string, date: string, libelle: string) {
    if (estVerrouille(date, verrou)) return surNotifier('Période clôturée : rouvrez l’exercice pour supprimer cette dépense')
    if (!window.confirm(`Supprimer la dépense « ${libelle} » ? Elle disparaîtra du journal.`)) return
    try {
      await supprimerDepense(orgId, id)
      surNotifier('Dépense supprimée')
      await surRafraichir()
    } catch (e) {
      surNotifier(`Erreur : ${e instanceof Error ? e.message : String(e)}`)
    }
  }

  return (
    <Carte
      titre="Charges et dépenses"
      sous="Carburant, livreurs, entretien, divers — passées par le serveur (record-expense)"
      actions={
        peut && (
          <button className="btn btn-gold btn-sm" onClick={() => setAjout(true)}>
            <Icone nom="plus" /> Dépense
          </button>
        )
      }
    >
      <Table
        entetes={['Date', 'Libellé', 'Compte', 'Montant', '']}
        droite={[3]}
        vide="Aucune charge saisie."
        lignes={donnees.expenses.map((e) => [
          dateCourte(e.spent_on),
          e.label,
          <CodeCompte code={COMPTE_DEPENSE[e.category] ?? '658'} />,
          <span className="mono">{fcfa(e.amount_fcfa)}</span>,
          peut ? (
            <button className="btn btn-ghost btn-sm" onClick={() => void supprimer(e.id, e.spent_on, e.label)} aria-label={`Supprimer ${e.label}`}>
              <Icone nom="x" />
            </button>
          ) : (
            ''
          ),
        ])}
      />
      {ajout && (
        <ModaleDepense
          verrou={verrou}
          surFermer={() => setAjout(false)}
          surEnregistrer={async (d) => {
            await enregistrerDepense(orgId, d)
            surNotifier('Charge ajoutée')
            setAjout(false)
            await surRafraichir()
          }}
        />
      )}
    </Carte>
  )
}

function ModaleDepense({
  verrou,
  surFermer,
  surEnregistrer,
}: {
  verrou: string | null
  surFermer: () => void
  surEnregistrer: (d: { category: string; label: string; amount_fcfa: number; spent_on: string }) => Promise<void>
}) {
  const [categorie, setCategorie] = useState<string>('fuel')
  const [libelle, setLibelle] = useState('')
  const [montant, setMontant] = useState(0)
  const [date, setDate] = useState(aujourdhui())
  const [erreur, setErreur] = useState<string | null>(null)
  const [enCours, setEnCours] = useState(false)

  async function enregistrer() {
    if (!libelle.trim()) return setErreur('Libellé requis.')
    if (!Number.isInteger(montant) || montant <= 0) return setErreur('Montant entier supérieur à zéro.')
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return setErreur('Date invalide.')
    if (estVerrouille(date, verrou)) return setErreur(`Période clôturée jusqu'au ${dateCourte(verrou)} : datez la dépense après, ou rouvrez l'exercice.`)
    setEnCours(true)
    setErreur(null)
    try {
      await surEnregistrer({ category: categorie, label: libelle.trim(), amount_fcfa: montant, spent_on: date })
    } catch (e) {
      setErreur(e instanceof Error ? e.message : String(e))
    } finally {
      setEnCours(false)
    }
  }

  return (
    <Modale titre="Nouvelle charge" ouverte surFermer={surFermer} surEnregistrer={() => void enregistrer()} enCours={enCours}>
      <div className="field">
        <label>Catégorie</label>
        <ListeDeroulante valeur={categorie} options={CATEGORIES_DEPENSE.map((c) => ({ valeur: c.valeur, libelle: c.libelle }))} surChangement={setCategorie} pleineLargeur />
      </div>
      <div className="field">
        <label htmlFor="dp-lib">Libellé</label>
        <input id="dp-lib" value={libelle} onChange={(e) => setLibelle(e.target.value)} placeholder="ex. Assurance flotte" />
      </div>
      <div className="field">
        <label htmlFor="dp-mt">Montant (F)</label>
        <input id="dp-mt" type="number" min={0} step={500} value={montant} onChange={(e) => setMontant(parseInt(e.target.value, 10) || 0)} />
      </div>
      <div className="field">
        <label htmlFor="dp-dt">Date</label>
        <input id="dp-dt" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
      </div>
      <p className="t-muted" style={{ fontSize: 12 }}>
        Seules ces quatre catégories existent en base. Pour une charge d’un autre compte (assurance 625, loyer 622…), passez
        une écriture manuelle.
      </p>
      {erreur && <p style={{ color: 'var(--err)', fontSize: 12.5 }}>{erreur}</p>}
    </Modale>
  )
}

/* -------------------------------------------------- écritures manuelles */

function EcrituresManuelles({ donnees, orgId, verrou, surNotifier, surRafraichir }: PropsCompta) {
  const droits = useDroits()
  const peut = droits.peut('ecritures') && donnees.synchro.documents
  const [ajout, setAjout] = useState(false)
  const plan = donnees.planComptable

  async function supprimer(id: string, date: string, piece: string) {
    if (estVerrouille(date, verrou)) return surNotifier('Période clôturée : rouvrez l’exercice pour supprimer cette écriture')
    if (!window.confirm(`Supprimer l'écriture ${piece} ? Préférez une contre-passation si elle a déjà été communiquée à l'expert-comptable.`)) return
    try {
      await supprimerEcritureManuelle(orgId, id)
      surNotifier('Écriture supprimée')
      await surRafraichir()
    } catch (e) {
      surNotifier(`Erreur : ${e instanceof Error ? e.message : String(e)}`)
    }
  }

  return (
    <Carte
      titre="Écritures manuelles"
      sous="Partie double libre — enregistrées en base, intégrées au journal, à la balance et au résultat"
      actions={
        peut && (
          <button className="btn btn-gold btn-sm" onClick={() => setAjout(true)}>
            <Icone nom="plus" /> Écriture
          </button>
        )
      }
    >
      <Table
        entetes={['Date', 'Pièce', 'Libellé', 'Débit → Crédit', 'Montant', '']}
        droite={[4]}
        vide="Aucune écriture manuelle."
        lignes={donnees.ecrituresManuelles.map((e) => [
          dateCourte(e.date),
          <span className="mono">{e.piece}</span>,
          e.libelle,
          <span title={`${libelleCompte(e.debit, plan)} → ${libelleCompte(e.credit, plan)}`}>
            <CodeCompte code={e.debit} /> → <CodeCompte code={e.credit} />
          </span>,
          <span className="mono">{fcfa(e.montant)}</span>,
          peut ? (
            <button className="btn btn-ghost btn-sm" onClick={() => void supprimer(e.id, e.date, e.piece)} aria-label={`Supprimer ${e.piece}`}>
              <Icone nom="x" />
            </button>
          ) : (
            ''
          ),
        ])}
      />
      {ajout && (
        <ModaleEcriture
          plan={plan}
          verrou={verrou}
          surFermer={() => setAjout(false)}
          surEnregistrer={async (e) => {
            const n = await ajouterEcritureManuelle(orgId, e)
            surNotifier(`Écriture ${n.piece} enregistrée`)
            setAjout(false)
            await surRafraichir()
          }}
        />
      )}
    </Carte>
  )
}

function ModaleEcriture({
  plan,
  verrou,
  surFermer,
  surEnregistrer,
}: {
  plan: Record<string, string>
  verrou: string | null
  surFermer: () => void
  surEnregistrer: (e: { date: string; journal: Journal; libelle: string; debit: string; credit: string; montant: number }) => Promise<void>
}) {
  const options = useMemo(() => planComplet(plan).map((c) => ({ valeur: c.code, libelle: `${c.code} — ${c.label}` })), [plan])
  const [e, setE] = useState({ date: aujourdhui(), journal: 'OD' as Journal, libelle: '', debit: '411', credit: '7061', montant: 0 })
  const [erreurs, setErreurs] = useState<string[]>([])
  const [enCours, setEnCours] = useState(false)

  async function enregistrer() {
    const motifs = controlerEcritureManuelle(e, verrou)
    setErreurs(motifs)
    if (motifs.length) return
    setEnCours(true)
    try {
      await surEnregistrer({ ...e, libelle: e.libelle.trim() })
    } catch (x) {
      setErreurs([x instanceof Error ? x.message : String(x)])
    } finally {
      setEnCours(false)
    }
  }

  return (
    <Modale titre="Écriture manuelle (partie double)" ouverte surFermer={surFermer} surEnregistrer={() => void enregistrer()} enCours={enCours}>
      <div className="grid-2" style={{ gap: 12 }}>
        <div className="field">
          <label htmlFor="em-date">Date</label>
          <input id="em-date" type="date" value={e.date} onChange={(x) => setE({ ...e, date: x.target.value })} />
        </div>
        <div className="field">
          <label>Journal</label>
          <ListeDeroulante
            valeur={e.journal}
            options={(Object.keys(LIBELLE_JOURNAL) as Journal[]).map((j) => ({ valeur: j, libelle: `${j} — ${LIBELLE_JOURNAL[j]}` }))}
            surChangement={(v) => setE({ ...e, journal: v as Journal })}
            pleineLargeur
          />
        </div>
      </div>
      <div className="field">
        <label htmlFor="em-lib">Libellé</label>
        <input id="em-lib" value={e.libelle} onChange={(x) => setE({ ...e, libelle: x.target.value })} placeholder="ex. Abonnement SaaS Bolt Logistics — octobre" />
      </div>
      <div className="field">
        <label>Compte débité</label>
        <ListeDeroulante valeur={e.debit} options={options} surChangement={(v) => setE({ ...e, debit: v })} pleineLargeur />
      </div>
      <div className="field">
        <label>Compte crédité</label>
        <ListeDeroulante valeur={e.credit} options={options} surChangement={(v) => setE({ ...e, credit: v })} pleineLargeur />
      </div>
      <div className="field">
        <label htmlFor="em-mt">Montant (F)</label>
        <input id="em-mt" type="number" min={0} step={500} value={e.montant} onChange={(x) => setE({ ...e, montant: parseInt(x.target.value, 10) || 0 })} />
      </div>
      {erreurs.length > 0 && (
        <ul style={{ color: 'var(--err)', fontSize: 12.5, paddingLeft: 18 }}>
          {erreurs.map((m) => (
            <li key={m}>{m}</li>
          ))}
        </ul>
      )}
    </Modale>
  )
}

/* ------------------------------------------------------- plan de comptes */

function PlanDeComptes({ donnees, orgId, surNotifier, surRafraichir }: PropsCompta) {
  const droits = useDroits()
  const peut = droits.peut('ecritures') && donnees.synchro.documents
  const plan = planComplet(donnees.planComptable)
  const [ajout, setAjout] = useState(false)
  const [filtre, setFiltre] = useState('')

  async function renommer(code: string, ancien: string, nouveau: string) {
    const v = nouveau.trim()
    if (!v || v === ancien) return
    try {
      await enregistrerCompte(orgId, code, v)
      surNotifier(`Compte ${code} renommé`)
      await surRafraichir()
    } catch (e) {
      surNotifier(`Erreur : ${e instanceof Error ? e.message : String(e)}`)
    }
  }

  const visibles = plan.filter((c) => !filtre.trim() || c.code.startsWith(filtre.trim()) || c.label.toLowerCase().includes(filtre.trim().toLowerCase()))

  return (
    <Carte
      titre="Plan de comptes"
      sous="Renommez un compte (le libellé officiel reste la référence) ou ajoutez un sous-compte SYSCOHADA"
      actions={
        peut && (
          <button className="btn btn-gold btn-sm" onClick={() => setAjout(true)}>
            <Icone nom="plus" /> Compte
          </button>
        )
      }
    >
      <div className="card-body" style={{ paddingBottom: 0 }}>
        <div className="field">
          <label htmlFor="pc-filtre">Filtrer</label>
          <input id="pc-filtre" value={filtre} onChange={(e) => setFiltre(e.target.value)} placeholder="Code ou libellé" />
        </div>
      </div>
      <div className="tbl-wrap" style={{ maxHeight: 360, overflow: 'auto' }}>
        <table>
          <thead>
            <tr>
              <th style={{ width: 90 }}>Code</th>
              <th>Libellé</th>
            </tr>
          </thead>
          <tbody>
            {visibles.map((c) => (
              <tr key={c.code}>
                <td>
                  <CodeCompte code={c.code} />
                </td>
                <td>
                  {peut ? (
                    <input
                      aria-label={`Libellé du compte ${c.code}`}
                      defaultValue={c.label}
                      onBlur={(e) => void renommer(c.code, c.label, e.target.value)}
                      style={{ width: '100%', padding: '6px 9px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13 }}
                    />
                  ) : (
                    c.label
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {ajout && (
        <ModaleCompte
          surFermer={() => setAjout(false)}
          surEnregistrer={async (code, libelle) => {
            await enregistrerCompte(orgId, code, libelle)
            surNotifier(`Compte ${code} ajouté`)
            setAjout(false)
            await surRafraichir()
          }}
        />
      )}
    </Carte>
  )
}

function ModaleCompte({ surFermer, surEnregistrer }: { surFermer: () => void; surEnregistrer: (code: string, libelle: string) => Promise<void> }) {
  const [code, setCode] = useState('')
  const [libelle, setLibelle] = useState('')
  const [erreur, setErreur] = useState<string | null>(null)
  const [enCours, setEnCours] = useState(false)
  async function enregistrer() {
    if (!codeCompteValide(code)) return setErreur('Code SYSCOHADA : 2 à 8 chiffres, commençant par la classe (1 à 9).')
    if (!libelle.trim()) return setErreur('Libellé requis.')
    setEnCours(true)
    try {
      await surEnregistrer(code.trim(), libelle.trim())
    } catch (e) {
      setErreur(e instanceof Error ? e.message : String(e))
    } finally {
      setEnCours(false)
    }
  }
  return (
    <Modale titre="Nouveau compte" ouverte surFermer={surFermer} surEnregistrer={() => void enregistrer()} enCours={enCours}>
      <div className="field">
        <label htmlFor="cp-code">Code SYSCOHADA</label>
        <input id="cp-code" value={code} inputMode="numeric" onChange={(e) => setCode(e.target.value)} placeholder="ex. 6252" />
      </div>
      <div className="field">
        <label htmlFor="cp-lib">Libellé</label>
        <input id="cp-lib" value={libelle} onChange={(e) => setLibelle(e.target.value)} placeholder="ex. Assurances flotte" />
      </div>
      {erreur && <p style={{ color: 'var(--err)', fontSize: 12.5 }}>{erreur}</p>}
    </Modale>
  )
}
